import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle } from "lucide-react";
import Button from "./Button";
import { images, translations, type Lang } from "../content";

export default function Hero({ lang }: { lang: Lang }) {
  const t = translations[lang].hero;
  const rtl = lang === "he";

  /* Gentle parallax: the photo drifts slower than the page. Transform only. */
  const sectionRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const imageY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const rise = (delay: number) => ({
    initial: { y: 30, opacity: 0 },
    animate: { y: 0, opacity: 1 },
    transition: { duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] as const },
  });

  return (
    <section
      ref={sectionRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Full-bleed photo behind a vertical wash, as on the original. */}
      <motion.div style={{ y: imageY }} className="absolute inset-0 z-0">
        <img
          src={images.heroBus}
          alt="S.T.Y. Travel"
          className="w-full h-full object-cover opacity-40"
          style={{ objectPosition: "20% 30%" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-background" />
      </motion.div>

      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="container mx-auto px-6 md:px-10 relative z-10 text-center"
        dir={rtl ? "rtl" : "ltr"}
      >
        <motion.h1
          {...rise(0)}
          className="display-xl text-gold-gradient text-5xl md:text-7xl lg:text-9xl mb-6"
          dir="ltr"
        >
          {t.title}
        </motion.h1>

        <motion.p
          {...rise(0.2)}
          className="text-xl md:text-3xl text-white/90 font-light mb-10 tracking-wide"
        >
          {t.subtitle}
        </motion.p>

        <motion.div {...rise(0.4)}>
          <Button
            className="pill h-auto px-8 py-4 md:px-11 md:py-4 text-base md:text-lg
                       shadow-[0_0_28px_rgba(212,175,55,0.28)] hover:shadow-[0_0_44px_rgba(212,175,55,0.45)]
                       hover:scale-[1.03] transition-all duration-300"
            onClick={() =>
              document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })
            }
          >
            <MessageCircle className="h-5 w-5" />
            {t.cta}
          </Button>
        </motion.div>
      </motion.div>

      <motion.div
        className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center p-1">
          <div className="w-1 h-2 bg-primary rounded-full" />
        </div>
      </motion.div>
    </section>
  );
}
